import { group, topic } from "./schema.js";

export const BUYER_META = {
  role: "buyer",
  title: "Buyer Manual",
  kicker: "Your companion for the path home",
  whyHeading: "Why This Helps You and Your Agent",
};

export const BUYER_TREE = [
  topic({
    slug: "start-here",
    title: "Start Here",
    terms: ["getting started", "overview", "how the process works", "checklist"],
    forYou:
      "This book is for buyers using REChecklist. It is not a video — that is the Guided Tour — and it is not a listing search. It is the calm companion you keep beside the checklist so you never feel stuck.",
    how: [
      "Sign in so your agent can match this checklist to you.",
      "Complete each DreamHome section: location, home type, lot, exterior, interior, lifestyle, and accessibility.",
      "Add special notes, deal-breakers, and anything unique to your household.",
      "Review buyer tips, then track your transaction from pre-approval to walkthrough.",
      "Submit the finished checklist to your agent from the last page.",
    ],
    why:
      "Buyers often describe a feeling (“something with character,” “not too busy,” “good area”) that is hard to search. This checklist turns those instincts into specific, Fair Housing–safe preferences — commute time, lot type, kitchen layout, accessibility needs, and more. Your agent can filter listings, plan tours, and write offers with fewer back-and-forth emails and fewer houses that were never going to fit.",
    examples:
      "If “good area” really means a quiet street near your sister and a first-floor bedroom for visiting family, say those two things in Location and Notes. Your agent can search that. A feeling they cannot.",
    tips: [
      "You can pause any time. A page that feels settled is enough for today.",
      "If a word is unfamiliar, use the search in this book. That is what it is for.",
    ],
    related: ["location", "notes", "complete"],
    href: "/start",
  }),

  group("Getting around", [
    topic({
      slug: "navigation",
      title: "Menus and pages",
      terms: ["menu", "header", "buyer journey", "home"],
      forYou:
        "The top menu is the same calm path on every page. You should never wonder how you got here, or how to get back.",
      how: [
        "Use Home or Main Dashboard when you want a breath.",
        "Use Buyer Journey when you are working the checklist.",
        "Use Messages when you need your agent.",
        "Use Information & Resources when you need the office card — it lives at the bottom of every page.",
      ],
      why:
        "A professional tool should feel findable. When the rooms stay in the same place, you spend your energy on the home, not on the software.",
      examples:
        "From Exterior, you can open Main Dashboard, then return to Buyer Journey. Information & Resources does not leave the page — it scrolls you to the footer.",
      tips: [
        "Seller Journey is a different carpet. It will not copy your buyer answers, and you should not mix the two.",
      ],
      related: ["main-dashboard", "information-resources", "start"],
      href: "/start",
    }),
    topic({
      slug: "main-dashboard",
      title: "Main Dashboard",
      terms: ["dashboard", "cards", "landing", "welcome"],
      forYou:
        "The Main Dashboard is a visual landing besides the homepage. It gives new guests engaging starting points for the checklist — or you can use the navigation menu at the top to get started.",
      how: [
        "Read the sentence on a card, then use the button.",
        "Buyer Journey is the working door.",
        "Messages needs you signed in as a buyer.",
        "Add to Home Screen is optional — a quiet icon, not an app store.",
      ],
      why:
        "People remember a porch with pictures. They forget a list of links. Starting points that feel inviting make it easier to begin, and easier to come back tomorrow.",
      examples:
        "Note also on the top menu there is a link to Information & Resources that is always available at the bottom of any page. The Dashboard and the menu agree with each other. That is on purpose.",
      tips: ["Hover a card’s photo — it lifts a little, like a page turning."],
      related: ["navigation", "information-resources", "start"],
      href: "/dashboard",
    }),
    topic({
      slug: "information-resources",
      title: "Information & Resources",
      terms: ["footer", "office card", "privacy", "support", "license"],
      forYou:
        "The office card, contact, privacy, terms, and notices live in one honest place: the footer of every page.",
      how: [
        "Use the top-menu link, or simply scroll to the bottom.",
        "Read the agent or broker card that fills from their profile.",
        "Open Support if you want this site’s help with your buyer role still attached.",
      ],
      why:
        "You should always know whom you are working with. License numbers are recorded by the office. REChecklist does not grant or certify a California DRE credential.",
      examples:
        "If the card looks empty, your agent has not filled Profile yet. Asking them is rapport, not a bug in your checklist.",
      tips: ["Notices are system updates. Conversation with your agent belongs in Messages."],
      related: ["navigation", "messaging", "main-dashboard"],
    }),
  ]),

  group("Buyer Journey", [
    topic({
      slug: "start",
      title: "Start",
      terms: ["welcome", "first page", "begin"],
      forYou:
        "Start is a greeting, not a quiz. You step onto the buyer carpet at a human pace.",
      how: [
        "Read the welcome.",
        "Continue to Location when you are ready.",
        "The step list above walks with you so you can see where you are without a contest.",
      ],
      why:
        "Beginnings set tone. This one is a companion, not a starting gun. Your agent would rather you begin kindly than rush a page you do not mean.",
      examples:
        "If you only have ten minutes, do Start and Location. Come back for Lot later. That is allowed.",
      tips: ["The Notes tab is already available. You may write on day one."],
      related: ["location", "notes", "start-here"],
      href: "/start",
    }),
    topic({
      slug: "location",
      title: "Location",
      terms: ["city", "neighborhood", "commute", "schools", "area"],
      forYou:
        "Location is where life happens. Everything else is decoration if the map is wrong.",
      how: [
        "Name the places you can love — more than one is allowed.",
        "Add commute, quiet, or “not that intersection at 5 p.m.” in Notes.",
        "Leave a box blank if you are unsure. Honest blanks search better than pretty guesses.",
      ],
      why:
        "“Good area” is a feeling. “North of the 8, not east of the college, twenty minutes from work” is something your agent can actually use. Fair Housing–safe specifics help them filter without asking you to explain a vibe in five emails.",
      examples:
        "If the real sentence is “close to my sister, quiet at night,” write that in Notes after you pick cities. Your agent will hold both.",
      tips: ["You already know Notes live on the left. Location is where that habit starts to pay rent."],
      related: ["notes", "home-type", "lifestyle"],
      href: "/location",
    }),
    topic({
      slug: "home-type",
      title: "Home type",
      terms: ["house", "condo", "townhome", "stories", "single story"],
      forYou:
        "Name the kind of home before you name the sofa. Type filters search more than paint color.",
      how: [
        "Choose what you can live with — house, condo, townhome, stories.",
        "If two types are possible, say both.",
        "Put the why in Notes: knees, a dog, a piano, a parent who visits.",
      ],
      why:
        "Your agent can search a range. They cannot search a maybe you never wrote down. Home type plus Access together tell them the vessel, not just the map.",
      examples:
        "Single-story because of knees is a Home type decision and an Access decision. Put the medical why in Notes so it is never treated as a style preference.",
      tips: ["If “condo, but with a yard feeling” is the real sentence, write that. The checkbox is only the start."],
      related: ["location", "accessibility", "lot"],
      href: "/home-type",
    }),
    topic({
      slug: "lot",
      title: "Lot",
      terms: ["yard", "slope", "view", "acreage", "garden"],
      forYou:
        "Lot is the land the house sits on — size, slope, privacy, and how you want to use outdoor ground.",
      how: [
        "Choose ranges that feel true.",
        "Add Notes for vegetable beds, dogs, a child on a bike, or a view you will not trade.",
        "Finish Lot with Exterior so the ground and the envelope stay one home in your mind.",
      ],
      why:
        "A perfect interior on a lot you hate becomes a regret. Yards are daily, not occasional. Your agent needs that early, before they fall in love with a listing photo of a kitchen.",
      examples:
        "“Flat enough for a child on a bike, fenced for the dog” is a Lot sentence. Put it in Notes after the checkboxes. Don’t forget Notes on the left for this page.",
      tips: ["Lot and Exterior talk to each other. Outdoor living is what you do on the ground Lot describes."],
      related: ["exterior", "lifestyle", "notes"],
      href: "/lot",
    }),
    group("Exterior", [
      topic({
        slug: "exterior",
        title: "Outdoor living",
        terms: ["porch", "patio", "pool", "curb", "yard time"],
        forYou:
          "Outdoor living is how you live outside — porch, patio, pool, shade — not only how the house looks from the street.",
        how: [
          "Use the checkboxes and buttons so your agent knows which features matter.",
          "Mark what you want and what you do not need.",
          "Then open Notes on the left and add anything a neighbor would mention on a walk-by.",
        ],
        why:
          "Curb and outdoor truth are how an agent filters listings before you ever step inside. A covered porch you will use three nights a week is a search filter. A maybe on patio size is not.",
        examples:
          "You want a covered porch and you do not need a pool. Mark both. Then Notes: “We eat outside three nights a week.” That sentence will steer the search more than a guess.",
        tips: ["Building systems on this same page is next. Finish outdoor living first if that is the story you woke up with."],
        related: ["building-systems", "lot", "notes"],
        href: "/exterior",
      }),
      topic({
        slug: "building-systems",
        title: "Building systems",
        terms: ["roof", "siding", "windows", "solar", "envelope"],
        forYou:
          "Building systems tell your agent how the house should be built — roof, siding, windows, solar — not only how it should feel.",
        how: [
          "On the Exterior checklist page, Building Systems section, use the checkboxes and buttons to let your agent know what features you want.",
          "If you have no preference, say so in Notes rather than clicking at random.",
          "Don’t forget to add any notes for this page in the Notes section on the left sidebar that pertains to this page.",
        ],
        why:
          "Search gets sharper when systems are named. Your agent is not a mind reader. Your agent will see everything, and this is extremely valuable in their search for the perfect home for you.",
        examples:
          "A metal roof you love, or a window style you cannot live with, belongs in Notes after the boxes. That is the gift behind the checkbox.",
        tips: ["You already practiced Notes on Location and Lot. Exterior is that habit looking at the house itself."],
        related: ["exterior", "notes", "how-agents-use-this"],
        href: "/exterior",
      }),
    ]),
    topic({
      slug: "interior",
      title: "Interior",
      terms: ["kitchen", "baths", "rooms", "layout", "piano"],
      forYou:
        "Interior is the inside as you actually live — kitchens, baths, bedrooms, and the flow between them — not as a magazine.",
      how: [
        "Mark needs and nice-to-haves.",
        "Use Notes for the piano wall, the office, the guest who stays a month.",
        "If you cook every night, say so after you choose kitchen size.",
      ],
      why:
        "Listings hide in adjectives. Your brief should hide in specifics. “We cook every night and the dog sits under the island” is a search sentence. “Open concept” is a hope.",
      examples:
        "Don’t forget Notes on the left for this page. If you are on Interior, write about the piano. Your agent will see everything.",
      tips: ["Walk Interior after Exterior so outside and inside stay one home in your mind."],
      related: ["lifestyle", "notes", "home-type"],
      href: "/interior",
    }),
    topic({
      slug: "lifestyle",
      title: "Lifestyle",
      terms: ["commute", "pets", "hobbies", "quiet", "evenings"],
      forYou:
        "Lifestyle puts life on the page so search is not only square footage — commute, animals, hobbies, and the feeling of a street at 7 p.m.",
      how: [
        "Answer honestly. A dog who barks at fences is a lifestyle fact, not an apology.",
        "If evening quiet matters more than a short commute, say both truths.",
        "Pair this page with Accessibility before you call the brief done.",
      ],
      why:
        "The right house on the wrong street is still the wrong house. Your agent can hold two truths at once. They cannot hold a truth you never wrote.",
      examples:
        "After you finish Lifestyle, you might message: “We added a note about a first-floor bedroom for visiting family.” That is enough. You do not need to restate the whole checklist.",
      tips: ["This page is for you. It is not a personality test."],
      related: ["accessibility", "location", "messaging"],
      href: "/lifestyle",
    }),
    topic({
      slug: "accessibility",
      title: "Accessibility",
      terms: ["access", "mobility", "first floor", "stairs", "single story"],
      forYou:
        "Accessibility is daily life: stairs, baths, parking, and the path from car to door. Name how you move so search does not guess.",
      how: [
        "Mark what you need.",
        "Add a note if a parent or guest changes the picture even part of the year.",
        "Treat this page with the same care as Location. It is not an afterthought.",
      ],
      why:
        "A beautiful listing that you cannot live in is not a match. Specific, Fair Housing–safe access needs help your agent filter with dignity — for you and for anyone who visits.",
      examples:
        "If a first-floor bedroom is for visiting family, say so in Notes after you check the Access boxes. We have already covered Notes — this is that habit, used on purpose.",
      tips: ["Complete Access when you know the household, even if the house is still a dream."],
      related: ["lifestyle", "home-type", "notes"],
      href: "/accessibility",
    }),
    topic({
      slug: "complete",
      title: "Complete",
      terms: ["finished", "submit", "summary", "send to agent"],
      forYou:
        "Complete is a calm landing, not a trophy screen. It gathers what you have already said so your agent receives a brief you are proud of.",
      how: [
        "Review. Return to any step that still feels thin.",
        "Read your Notes tab once more.",
        "When it feels ready, submit the finished checklist to your agent from this last page.",
      ],
      why:
        "Professionals notice when a client finishes with care. A complete brief means fewer houses that were never going to fit, and fewer emails that start with “wait, I forgot to mention.”",
      examples:
        "If Location is solid but Lifestyle is empty, go back. Complete will wait. There is no race.",
      tips: ["This is a pause, not a goodbye. You can still update pages later."],
      related: ["start-here", "notes", "file-packet"],
      href: "/complete",
    }),
  ]),

  group("Companion tools", [
    topic({
      slug: "notes",
      title: "Notes",
      terms: ["deal breakers", "must haves", "household", "special notes", "sidebar"],
      forYou:
        "Notes say what a checkbox cannot. They are the letter you are writing your agent while you click, and they travel with the file.",
      how: [
        "Open the Notes tab on the left companion.",
        "Use the gentle fields for deal-breakers, must-haves, household needs, and special notes.",
        "Write as if your agent will read it tomorrow — because they will.",
      ],
      why:
        "Checkboxes without notes are a silhouette. Notes are the face. Your agent cannot guess the piano wall, the Sunday visitor, or the street you will not consider. Notes are how you give them that gift.",
      examples:
        "On the Exterior page, after you mark a covered porch, add: “Grandparents sit outside in summer; shade is a must.” Don’t forget to add any notes for this page in the Notes section on the left sidebar that pertains to this page.",
      tips: [
        "This habit is mentioned throughout the book on purpose. It is the spine of a good brief.",
        "Notes stay on the buyer journey. They never spill into a seller portfolio.",
      ],
      related: ["tips", "building-systems", "how-agents-use-this"],
      href: "/notes",
    }),
    topic({
      slug: "tips",
      title: "Tips",
      terms: ["buyer tips", "reminders", "sidebar"],
      forYou:
        "Tips sit beside the page as a gentle coach — not a lecture. They are the pocket version of this book.",
      how: [
        "Open the Tips tab when you want a short nudge.",
        "Return to Notes or Transaction when you are working.",
        "Review tips before you treat the brief as finished.",
      ],
      why:
        "Some people learn by doing. Tips keep the doing kind, so you never feel stuck between checklist pages and this longer manual.",
      examples:
        "A tip might remind you that notes travel with the file. You have already read that here — the tab is the same kindness, closer to the click.",
      tips: ["Tips are optional. The checklist works without them. They are there so you never feel alone on the page."],
      related: ["notes", "transaction", "start-here"],
    }),
    topic({
      slug: "transaction",
      title: "Transaction steps",
      terms: ["closing", "earnest", "escrow", "walk-through", "pre-approval"],
      forYou:
        "The Transaction tab lists educational beats from pre-approval to walkthrough. It is a companion, not legal advice. Your escrow officer still leads the file.",
      how: [
        "Open Transaction in the left companion when you are ready — you do not have to finish it before Location.",
        "Read the steps in order.",
        "Ask your agent in Messages about anything that feels foggy.",
      ],
      why:
        "Closing should not be a surprise. Naming the beats early means fewer panicked questions at the wrong hour, and more rapport in the right room.",
      examples:
        "“Could you walk me through earnest money on the Transaction tab?” is a perfect message. Your agent can answer, and that question builds trust.",
      tips: ["Educational primers also live on this site. They are not a substitute for your officer."],
      related: ["messaging", "complete", "tips"],
      href: "/transaction",
    }),
  ]),

  group("Staying in touch", [
    topic({
      slug: "messaging",
      title: "Messaging",
      terms: ["messages", "thread", "inbox", "ask", "rapport"],
      forYou:
        "Messages is a quiet thread with your agent about your file — not a public inbox, and not the room where the office talks to itself.",
      how: [
        "Open Messages from the top menu once you are signed in.",
        "Write in complete thoughts. One clear question is enough.",
        "Attachments follow gentle size limits (documents under 2 MB; photos may compress on your device).",
      ],
      why:
        "Rapport grows when questions have a home. Your agent can answer here without mixing seller files, and without five scattered texts that never make it into the brief.",
      examples:
        "After Lifestyle, send: “We added a note about a first-floor bedroom for visiting family.” That is enough. You do not need to restate the whole checklist.",
      tips: [
        "Notices are the product speaking. Messages are you and your agent speaking.",
        "If you ever feel unsure, search this book, then ask. That is how professionals work together.",
      ],
      related: ["notes", "transaction", "calendar"],
      href: "/client/messages",
    }),
    topic({
      slug: "calendar",
      title: "Calendar",
      terms: ["showings", "appointments", "dates", "tours"],
      forYou:
        "Showings and key dates live on your agent’s desk, with the file — not as a public scoreboard.",
      how: [
        "If you need a time, write it in Messages.",
        "Your agent places it on the desk calendar so it stays with the file.",
        "Confirm in the same thread when a showing is set.",
      ],
      why:
        "A date that lives only in memory is how weekends get tangled. Dates that live with the file mean fewer “wait, was that Saturday?” messages, and tours that actually fit your life.",
      examples:
        "“Thursday after 4 p.m. is hard because of pickup” is a calendar note dressed as a message. “Could we see Saturday morning in North Park?” is a request, not a checklist change.",
      tips: ["Community events on this marketing site are optional and separate from your file dates."],
      related: ["messaging", "file-packet"],
    }),
  ]),

  group("Your file", [
    topic({
      slug: "file-packet",
      title: "File Packet",
      terms: ["packet", "brief", "file", "your file"],
      forYou:
        "You do not assemble the File Packet. Your agent does. You contribute by completing pages, writing notes, and keeping the thread kind.",
      how: [
        "Save checklist pages as you go. They become the living brief.",
        "Your agent opens Files on their desk to see profile, checklist, timeline, messages, and calendar together.",
        "Ask them to walk you through the packet once if you are curious. This book is not a second desk.",
      ],
      why:
        "Sticky notes and paper planners scatter. A packet does not. You leave with something usable — and your agent leaves with a brief they can actually work.",
      examples:
        "When you finish Complete, a good agent can already speak your porch, your lot, and your notes without shuffling paper.",
      tips: ["You do not need a second copy. The original lives with the office."],
      related: ["complete", "how-agents-use-this", "notes"],
    }),
    topic({
      slug: "how-agents-use-this",
      title: "How your agent uses what you add",
      terms: ["search", "tours", "offers", "filter", "agent"],
      forYou:
        "Every checkbox and note is a sentence you are sending into the search. This page is the why behind the work.",
      how: [
        "Your agent reads the checklist and Notes together.",
        "They filter listings with Fair Housing–safe facts — commute, lot, kitchen, access — not with a vibe they have to guess.",
        "They plan tours and write offers with fewer houses that were never going to fit.",
      ],
      why:
        "Buyers often describe a feeling that is hard to search. This checklist turns those instincts into specifics. Fewer back-and-forth emails. More houses that might actually be home.",
      examples:
        "If you marked a covered porch and wrote that grandparents need shade, that is already a search filter. Thank-you notes belong in Messages; the work is already in the file.",
      tips: ["The more true the Notes, the kinder the tours. Honesty is easier to search than a full page of maybe."],
      related: ["notes", "building-systems", "file-packet"],
    }),
  ]),
];
